/**
 * pdp-fitment.js
 *
 * PDP companion to compatible-bikes-filter.js. Mirrors the same
 * bike-selection state model (URL ?bike=, localStorage key
 * `forty5_selected_bike`, custom event `forty5:bike-selected`) and
 * renders a fitment tag below the buy box.
 *
 * Mounts onto: <div data-pdp-fitment data-compatible-bikes="..."></div>
 *
 * States:
 *   No bike selected           → "Check fit with your bike" button (opens the selector)
 *   Bike selected, fits        → .f5-tag.f5-tag--fits "Fits your 2022 Road Glide ST"
 *   Bike selected, not listed  → neutral note "Not confirmed for your … yet" +
 *                                "Ask us on WhatsApp". Never "doesn't fit":
 *                                supplier fitment data is incomplete, so a
 *                                missing match is not a no.
 *
 * Universal/no-data products: this script never mounts because the
 * Liquid that renders the placeholder only emits the div when the
 * `custom.compatible_bikes` metafield is present on the product.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'forty5_selected_bike';
  const URL_PARAM = 'bike';
  const SELECTED_EVENT = 'forty5:bike-selected';
  const TARGET_SELECTOR = '[data-pdp-fitment]';
  const BIKES_CACHE_KEY = 'forty5_bikes_cache_v2'; /* written by motorcycle-selector.js */
  const LABEL_KEY = 'forty5_selected_bike_label'; /* "2022 Road Glide ST" */
  const WHATSAPP = 'https://wa.me/971585269568';

  /* ---------------------------------------------------------------- */
  /* Selection lookup — same precedence as compatible-bikes-filter.js */
  /* ---------------------------------------------------------------- */

  function readInitialSelection() {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get(URL_PARAM);
      if (fromUrl) return fromUrl;
    } catch (_e) {
      /* SSR or sandboxed iframe — ignore */
    }
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (_e) {
      return null;
    }
  }

  /* ---------------------------------------------------------------- */
  /* Bike list parsing — JSON array OR pipe-separated string          */
  /* ---------------------------------------------------------------- */

  function parseCompatibleBikes(raw) {
    if (!raw) return [];
    const trimmed = String(raw).trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const arr = JSON.parse(trimmed);
        if (Array.isArray(arr)) return arr.map(String).map((s) => s.trim()).filter(Boolean);
      } catch (_e) {
        /* fall through to pipe split */
      }
    }
    return trimmed.split('|').map((s) => s.trim()).filter(Boolean);
  }

  /* ---------------------------------------------------------------- */
  /* Bike label — pretty-print a slug for the tag text                */
  /* ---------------------------------------------------------------- */

  function bikeLabel(slug) {
    if (!slug) return '';
    try {
      if (localStorage.getItem(STORAGE_KEY) === slug) {
        const label = localStorage.getItem(LABEL_KEY);
        if (label) return label;
      }
    } catch (_e) {
      /* storage unavailable — fall through */
    }
    /* Prefer the canonical display_name from the selector's cached bike
     * list; fall back to de-slugging ("2022-road-glide-st" → "2022 road glide st"). */
    try {
      const bikes = JSON.parse(sessionStorage.getItem(BIKES_CACHE_KEY) || '[]');
      const match = bikes.find((b) => b.id === slug);
      if (match && match.display_name) return match.display_name;
    } catch (_e) {
      /* cache unavailable — fall through */
    }
    return String(slug).replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function openSelector() {
    const selector = document.querySelector('motorcycle-selector');
    const trigger = selector && selector.querySelector('.motorcycle-selector__trigger');
    if (!trigger) return;
    selector.scrollIntoView({ behavior: 'smooth', block: 'start' });
    trigger.click();
  }

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */

  function render(target, selectedBike) {
    if (!target) return;

    if (!selectedBike) {
      /* No bike yet — invite the check instead of showing nothing. */
      if (!document.querySelector('motorcycle-selector')) {
        target.replaceChildren();
        return;
      }
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'button button--secondary button--full-width';
      btn.textContent = 'Check fit with your bike';
      btn.addEventListener('click', openSelector);
      target.replaceChildren(btn);
      return;
    }

    const compatible = parseCompatibleBikes(target.getAttribute('data-compatible-bikes'));
    const name = bikeLabel(selectedBike);

    /* Build via DOM so user-supplied bike slugs cannot inject markup. */
    if (compatible.includes(selectedBike)) {
      const tag = document.createElement('span');
      tag.className = 'f5-tag f5-tag--fits';
      tag.setAttribute('role', 'status');
      tag.textContent = 'Fits your ' + name;
      target.replaceChildren(tag);
      return;
    }

    const note = document.createElement('div');
    note.className = 'f5-fit-note';
    note.setAttribute('role', 'status');
    const text = document.createElement('p');
    text.textContent = 'Not confirmed for your ' + name + ' yet. Many parts fit more bikes than the supplier lists.';
    const ask = document.createElement('a');
    ask.className = 'f5-fit-note__ask';
    const product = target.getAttribute('data-product-title') || document.title;
    ask.href = WHATSAPP + '?text=' + encodeURIComponent('Hi, does "' + product + '" fit my ' + name + '? ' + window.location.href);
    ask.target = '_blank';
    ask.rel = 'noopener';
    ask.textContent = 'Ask us on WhatsApp';
    note.append(text, ask);
    target.replaceChildren(note);
  }

  /* ---------------------------------------------------------------- */
  /* Boot                                                              */
  /* ---------------------------------------------------------------- */

  function init() {
    const targets = document.querySelectorAll(TARGET_SELECTOR);
    if (!targets.length) return;

    const initialBike = readInitialSelection();
    targets.forEach((t) => render(t, initialBike));

    document.addEventListener(SELECTED_EVENT, function (ev) {
      /* motorcycle-selector dispatches { bike: {id, display_name, …} | null } */
      const detail = ev && ev.detail;
      const bike = detail && 'bike' in detail ? (detail.bike ? detail.bike.id : null) : readInitialSelection();
      /* label is written to storage by the selector before it dispatches */
      document.querySelectorAll(TARGET_SELECTOR).forEach((t) => render(t, bike));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
